import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { GetRowIdParams } from 'ag-grid-enterprise'
import { isAfter, isWeekend } from 'date-fns'
import { Mail, Play } from 'lucide-react'

import { isValidDate, toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { getBasicColDefsFromData } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { FiltersProvider, useApi, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Attributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { RunFixedIncomeModal } from '@/components/dialogs/RunFixedIncomeModal'
import { PM } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { useSendFixedIncomeEmailMutation } from '@/react-query/mutations/attributions'

const stateColDefs = createColDefs([
  {
    width: 10,
    field: 'id',
    hide: true,
  },
  {
    width: 100,
    field: 'run_id',
    rowGroup: true,
    hide: true,
  },
  {
    width: 160,
    field: 'upload_datetime_utc',
    headerName: 'Upload UTC',
    sort: 'desc',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 100,
    field: 'crashed',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 250,
    field: 'key',
    headerName: 'Name',
  },
  {
    width: 90,
    field: 'step',
    headerName: '#Step',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 500,
    field: 'output',
  },
])

const filters = createFilters([
  { type: 'date', name: 'value_date', defaultValue: undefined, params: true },
])

export interface FixedIncomePageProps {}

export const FixedIncomePage: React.FC<FixedIncomePageProps> = ({}) => {
  return (
    <Page
      metadata={{ title: 'Fixed Income' }}
      permissions={PM.ATTRIBUTIONS.FIXED_INCOME.permissions}
    >
      <FiltersProvider filters={filters}>
        <FixedIncome />
      </FiltersProvider>
    </Page>
  )
}

export interface FixedIncomeProps {}

export const FixedIncome: React.FC<FixedIncomeProps> = ({}) => {
  const { registerFilter, values } = useFilters<typeof filters>()

  const [running, setRunning] = useState(false)

  const sendEmailMutation = useSendFixedIncomeEmailMutation()

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['attributions', 'fixed-income', 'state'],
        queryFn: () => api.attributions.fixedIncome.getState(),
        refetchInterval: 7_500,
      },
      {
        queryKey: [
          'attributions',
          'fixed-income',
          'final-attributions',
          { value_date: values.value_date ? toISO8601(values.value_date) : null },
        ],
        queryFn: () =>
          api.attributions.fixedIncome.getFinalAttributions({
            value_date: values.value_date ? toISO8601(values.value_date) : null!,
          }),
        enabled: isValidDate(values.value_date),
      },
    ],
  })

  const [state, finalAttributions] = queries

  const [stateData, finalAttributionsData] = useMemo(() => {
    return [state.data ?? [], finalAttributions.data ?? []]
  }, [state, finalAttributions])

  return (
    <>
      <RunFixedIncomeModal open={running} onOpenChange={setRunning} />

      <HeaderConfigBar>
        <div className="flex items-end gap-2">
          <FormItem>
            <Label>Report Date</Label>
            <DatePickerWithControls
              controlsDisabled={!isValidDate(values.value_date)}
              disabled={(date) => isWeekend(date) || isAfter(date, new Date())}
              {...registerFilter('value_date')}
              size={'sm'}
            />
          </FormItem>
          <CanAccess permissions={[PERMISSIONS.WRITE_FIXED_INCOME]}>
            <LoadingButton
              size="sm"
              variant={'outline'}
              onClick={() =>
                sendEmailMutation.mutateAsync({
                  value_date: values.value_date ? toISO8601(values.value_date) : '',
                })
              }
            >
              <Mail />
              Send email
            </LoadingButton>
            <Button size="sm" variant={'outline'} onClick={() => setRunning(true)}>
              <Play />
              Run Fixed Income
            </Button>
          </CanAccess>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel>
          <Widget className={'h-full'}>
            <WidgetHeaderWithQuery title={'FI Attribution State (refresh 7s)'} query={state} />
            <WidgetContent loading={state.isLoading}>
              <CustomAgGrid
                getRowId={(
                  params: GetRowIdParams<Attributions.GetFixedIncomeStateData[number]>,
                ) => {
                  return params.data.id.toString()
                }}
                domLayout={'normal'}
                rowData={stateData}
                columnDefs={stateColDefs}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <Widget className={'h-full'}>
            <WidgetHeaderWithQuery
              title={
                'FI Final Attribution - showing first 25 columns from the existing 98 (refresh via picking a report date)'
              }
              query={finalAttributions}
            />
            <WidgetContent loading={finalAttributions.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                rowData={finalAttributionsData}
                columnDefs={getBasicColDefsFromData(finalAttributionsData)}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </>
  )
}
